import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";

const initial = (s) => (s || "?").trim().charAt(0).toUpperCase();
const text = (r) => (typeof r === "string" ? r : r?.text || r?.name || "");
const PROVENANCE = { manual: "Human-written role", ai: "AI-assisted draft · human-reviewed" };

export const postedAgo = (iso) => {
  const t = Date.parse(iso || "");
  if (!t) return "";
  const d = Math.floor((Date.now() - t) / 864e5);
  if (d <= 0) return "Posted today";
  if (d === 1) return "Posted yesterday";
  if (d < 14) return `Posted ${d} days ago`;
  return d < 60 ? `Posted ${Math.floor(d / 7)} weeks ago` : `Posted ${Math.floor(d / 30)} months ago`;
};

function Tag({ icon: Icon, children, testId }) {
  return <span className="inline-flex items-center gap-1.5 rounded-md bg-foreground/[0.06] px-2 py-1 text-xs text-foreground/80" data-testid={testId}>{Icon && <Icon className="h-3 w-3" />}{children}</span>;
}

export function RoleCard({ job, company, onOpen, testId }) {
  const org = job.company_name || company;
  return (
    <button onClick={onOpen} data-testid={testId}
      className="group text-left rounded-2xl border border-border bg-card p-5 flex flex-col gap-3 min-w-0 transition-[border-color,transform] duration-200 hover:border-primary/40 hover:-translate-y-0.5">
      <span className="flex items-center gap-3 min-w-0">
        <span className="h-10 w-10 rounded-xl bg-foreground/[0.06] flex items-center justify-center font-display font-semibold shrink-0" aria-hidden>{initial(org || job.title)}</span>
        <span className="min-w-0">
          <span className="block text-xs text-muted-foreground truncate">{[org, job.department].filter(Boolean).join(" · ")}</span>
          <span className="block font-display text-[17px] font-semibold leading-snug truncate transition-colors group-hover:text-primary">{job.title}</span>
        </span>
      </span>
      {job.summary && <span className="text-sm leading-6 text-muted-foreground line-clamp-2">{job.summary}</span>}
      <span className="flex flex-wrap gap-1.5">{[job.location, job.employment_type, job.seniority].filter(Boolean).map((t) => <Tag key={t}>{t}</Tag>)}</span>
      <span className="mt-auto pt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>{postedAgo(job.published_at || job.created_at)}</span>
        <span className="text-sm font-medium text-primary inline-flex items-center gap-1">View role<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
      </span>
    </button>
  );
}

function Block({ title, children, testId }) {
  return <section className="space-y-3" data-testid={testId}><h2 className="font-display text-lg font-semibold">{title}</h2>{children}</section>;
}

function Bullets({ items, testId }) {
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((r, i) => <li key={i} className="flex gap-3 text-[15px] leading-7 text-foreground/85"><span className="mt-[11px] h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{text(r)}</li>)}
    </ul>
  );
}

function ApplyCard({ user, onApply, testId }) {
  const nav = useNavigate();
  const candidate = user?.role === "candidate";
  return (
    <aside className="sticky bottom-3 z-20 lg:bottom-auto lg:top-24" data-testid="role-apply-card">
      <div className="rounded-2xl border border-primary/30 bg-card/95 backdrop-blur-xl p-3 lg:p-6 space-y-4 shadow-2xl shadow-black/50 lg:shadow-none">
        <div className="hidden lg:block space-y-1.5">
          <h2 className="font-display text-lg font-semibold">Apply for this role</h2>
          <p className="text-sm text-muted-foreground leading-6">{candidate ? "Apply with your HireStream profile." : "Sign in to apply with your HireStream profile. New here? Create an account first."}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="lg" className="w-full gap-2 font-semibold" onClick={onApply} data-testid={testId}>{candidate ? "Apply now" : "Sign in to apply"}<ArrowRight className="h-4 w-4" /></Button>
          {!user && <Button size="lg" variant="outline" className="w-full hidden lg:inline-flex" onClick={() => nav("/login?mode=signup")} data-testid="role-create-account">Create an account</Button>}
        </div>
        <p className="hidden lg:flex items-start gap-2 text-xs text-muted-foreground leading-5"><ShieldCheck className="h-4 w-4 text-primary shrink-0" />Assessments are AI-assisted. A person reviews every application.</p>
      </div>
    </aside>
  );
}

// Public role page: the role on the left, the apply card pinned on the right (a bottom bar on phones).
export function RoleView({ job, company, user, onApply, applyTestId }) {
  const org = job.company_name || company;
  const resp = job.responsibilities || [];
  const reqs = job.requirements || [];
  const comps = (job.competencies || []).filter((c) => c?.name);
  return (
    <div className="flex flex-col gap-10 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-12 lg:items-start" data-testid="job-detail">
      <article className="min-w-0 space-y-9">
        <header className="space-y-5">
          <div className="flex items-center gap-4 min-w-0">
            <span className="h-14 w-14 rounded-2xl bg-foreground/[0.06] flex items-center justify-center font-display text-xl font-semibold shrink-0" aria-hidden>{initial(org || job.title)}</span>
            <div className="min-w-0">
              {(org || job.department) && <p className="text-sm text-muted-foreground" data-testid="job-detail-company">{[org, job.department].filter(Boolean).join(" · ")}</p>}
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight [overflow-wrap:anywhere]" data-testid="job-detail-title">{job.title}</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {job.location && <Tag icon={MapPin}>{job.location}</Tag>}
            {[job.employment_type, job.seniority].filter(Boolean).map((t) => <Tag key={t}>{t}</Tag>)}
            <Tag testId="job-detail-provenance">{PROVENANCE[job.provenance] || "Authorship unverified"}</Tag>
          </div>
          {job.summary && <p className="text-base leading-8 text-foreground/85" data-testid="job-detail-summary">{job.summary}</p>}
        </header>
        {resp.length > 0 && <Block title="What you'll do"><Bullets items={resp} testId="job-detail-responsibilities" /></Block>}
        {reqs.length > 0 && <Block title="What we're looking for"><Bullets items={reqs} testId="job-detail-requirements" /></Block>}
        {comps.length > 0 && (
          <Block title="How we'll assess" testId="job-detail-competencies">
            <p className="text-sm text-muted-foreground -mt-1">The assessment is scored on these skills. A person reviews every result.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {comps.map((c) => (
                <div key={c.name} className="rounded-xl border border-border bg-card px-4 py-3">
                  {c.weight != null && <div className="hs-mono text-lg font-semibold text-primary">{c.weight}%</div>}
                  <div className="text-sm leading-snug text-foreground/85 mt-0.5 [overflow-wrap:anywhere]">{c.name}</div>
                </div>
              ))}
            </div>
          </Block>
        )}
      </article>
      <ApplyCard user={user} onApply={onApply} testId={applyTestId} />
    </div>
  );
}
